<script setup>
import { computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  inboxId: { type: Number, default: null },
});

// { reply_assignment, reply_assignee_id, reply_team_id }, no formato da API
const assignment = defineModel({ type: Object, required: true });

const INBOX_DEFAULT = 'inbox_default';
const ROUND_ROBIN = 'round_robin';
const HELP_KEYS = {
  inbox_default: 'INBOX_DEFAULT',
  agent: 'AGENT',
  team: 'TEAM',
  round_robin: 'ROUND_ROBIN',
};

const store = useStore();
const teams = useMapGetter('teams/getTeams');
const assignableAgents = useMapGetter(
  'inboxAssignableAgents/getAssignableAgents'
);
const agents = computed(() =>
  props.inboxId ? assignableAgents.value(props.inboxId) : []
);

const fetchAgents = () => {
  if (props.inboxId)
    store.dispatch('inboxAssignableAgents/fetch', [props.inboxId]);
};

onMounted(() => {
  store.dispatch('teams/get');
  fetchAgents();
});

// O agente precisa participar da caixa de entrada; ao trocar a caixa, a escolha volta ao padrão.
watch(
  () => props.inboxId,
  (newInboxId, oldInboxId) => {
    fetchAgents();
    if (oldInboxId && assignment.value.reply_assignment === 'agent')
      assignment.value = { reply_assignment: INBOX_DEFAULT };
  }
);

// Um select só, com o tipo e o id juntos no valor da opção (ex.: "agent:12").
const selected = computed({
  get() {
    const {
      reply_assignment: type,
      reply_assignee_id: assigneeId,
      reply_team_id: teamId,
    } = assignment.value;
    if (type === 'agent') return `agent:${assigneeId}`;
    if (type === 'team') return `team:${teamId}`;
    return type || INBOX_DEFAULT;
  },
  set(value) {
    const [type, id] = value.split(':');
    assignment.value = {
      reply_assignment: type,
      reply_assignee_id: type === 'agent' ? Number(id) : null,
      reply_team_id: type === 'team' ? Number(id) : null,
    };
  },
});

const helpKey = computed(
  () => HELP_KEYS[assignment.value.reply_assignment || INBOX_DEFAULT]
);
</script>

<template>
  <label>
    {{ $t('CAMPAIGN.ADD.FORM.REPLY_ASSIGNMENT.LABEL') }}
    <select v-model="selected">
      <option :value="INBOX_DEFAULT">
        {{ $t('CAMPAIGN.ADD.FORM.REPLY_ASSIGNMENT.INBOX_DEFAULT_OPTION') }}
      </option>
      <option :value="ROUND_ROBIN">
        {{ $t('CAMPAIGN.ADD.FORM.REPLY_ASSIGNMENT.ROUND_ROBIN_OPTION') }}
      </option>
      <optgroup
        v-if="teams.length"
        :label="$t('CAMPAIGN.ADD.FORM.REPLY_ASSIGNMENT.TEAMS_GROUP')"
      >
        <option v-for="team in teams" :key="team.id" :value="`team:${team.id}`">
          {{ team.name }}
        </option>
      </optgroup>
      <optgroup
        v-if="agents.length"
        :label="$t('CAMPAIGN.ADD.FORM.REPLY_ASSIGNMENT.AGENTS_GROUP')"
      >
        <option
          v-for="agent in agents"
          :key="agent.id"
          :value="`agent:${agent.id}`"
        >
          {{ agent.name }}
        </option>
      </optgroup>
    </select>
    <p class="-mt-2 mb-4 text-xs text-n-slate-11">
      {{ $t(`CAMPAIGN.ADD.FORM.REPLY_ASSIGNMENT.HELP.${helpKey}`) }}
    </p>
  </label>
</template>
